import { IScope, IPromise, IQService, ITimeoutService } from "angular";
import { StateService, StateParams } from "@uirouter/angularjs";
import { IModalService, IModalScope } from "angular-ui-bootstrap";
import { TaskService } from "../services/taskService";
import { ITask, TaskParentType, TaskSort, TasksViewMode } from "../models/models";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalService } from "@gtmhub/goals/goal.service";
import { HubService } from "../../hub/services/hub";
import { IHubFilter } from "../../hub/models/hub";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { IStateInit } from "@gtmhub/core/routing";
import { ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs, TaskEvents } from "../taskEvents";
import { AssigneeActions, IAssigneeIdMap, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { filterActiveSessionsAllStatuses, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountType, IRestLayerRequest } from "@gtmhub/core";
import { getCurrentUserId } from "@gtmhub/users";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { IGoal } from "@gtmhub/goals/models";

interface ITasksRenderingStep {
  step: number;
}
interface ITasksRenderingLimit {
  limitTo: number;
}
interface ITasksListRenderingOptions extends ITasksRenderingStep, ITasksRenderingLimit {}
interface ITaskKanbanRenderingOptions extends ITasksRenderingStep {
  todo: ITasksRenderingLimit;
  inprogress: ITasksRenderingLimit;
  done: ITasksRenderingLimit;
}
type GtmhubTaskStatus = "todo" | "inprogress" | "done";

interface ITasksScope extends IScope {
  tasksColumns: {
    done: ITask[];
    inprogress: ITask[];
    todo: ITask[];
  };
  dueDate: string;
  taskList: ITask[];
  //Used in order to render empty-state only in case there are neither archived tasks nor active ones
  archivedTasksCount: number;
  mode: TasksViewMode;
  indicators: {
    loadingTasks: boolean;
  };
  assigneesIdMap: IAssigneeIdMap;
  currentSessionsIds: string[];
  isCreatingNewKanbanTask: boolean;
  isSortedBy: {
    name: boolean;
    dueDate: boolean;
    status: boolean;
  };
  isAscSort: boolean;
  renderingOptions: {
    list: ITasksListRenderingOptions;
    kanban: ITaskKanbanRenderingOptions;
  };
  isTaskCellHovered: boolean;
  isDemoAccount: boolean;
  isBannerEnabled: boolean;
  toggleTaskCellHover(isHovered: boolean): void;
  toggleMode(mode: TasksViewMode): void;
  dropped(event, index, type, external, column: string): void;
  deleteTask(task: ITask): void;
  archive(task: ITask): void;
  clone(task: ITask): void;
  createNewTaskInList(): void;
  openTaskFormInKanban(): void;
  setDueDate(task: ITask, date: string): void;
  sortBy(sort: string, changeAscDesSort?: boolean): void;
  increaseListLimit(): void;
  increaseKanbanLimit(columnName: GtmhubTaskStatus): void;
  favoritesManager: FavoritesManager;
  getNavItem(list: ITask): INavItem;
  taskIsFavorite(list: ITask): boolean;
  isNewExperienceEnabled(): boolean;
}

const sizes = {
  taskRowHeight: 39,
  taskKanbanHeight: 130,
};

export class TasksCtrl implements IStateInit {
  private taskEvents: TaskEvents;
  private sortTask: TaskSort;
  private favoritesManager: FavoritesManager;

  public static $inject = [
    "$scope",
    "$rootScope",
    "$state",
    "$q",
    "CurrentUserService",
    "UIErrorHandlingService",
    "AssigneeActions",
    "PlanningSessionsActions",
    "GoalService",
    "$stateParams",
    "$uibModal",
    "HubService",
    "TaskService",
    "$timeout",
    "$ngRedux",
    "MetricService",
    "AccountResolverService",
    "FavoritesManagerFactory",
    "FeatureToggleService",
    "AccountService",
  ];

  constructor(
    private $scope: ITasksScope,
    private $rootScope: ITraceRootScopeService,
    private $state: StateService,
    private $q: IQService,
    private currentUserService: CurrentUserService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private planningSessionsActions: PlanningSessionsActions,
    private goalService: GoalService,
    private $stateParams: StateParams,
    private $uibModal: IModalService,
    private hubService: HubService,
    private tasksService: TaskService,
    private $timeout: ITimeoutService,
    private $ngRedux: INgRedux,
    private metricService: MetricService,
    private accountResolverService: AccountResolverService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private featureToggleService: FeatureToggleService,
    private accountService: AccountService
  ) {
    this.taskEvents = new TaskEvents(this.$scope);
    this.favoritesManager = this.favoritesManagerFactory(["task"]);

    this.$scope.indicators = {
      loadingTasks: false,
    };

    this.$scope.isSortedBy = {
      name: false,
      dueDate: false,
      status: false,
    };

    this.$scope.tasksColumns = {
      done: [],
      inprogress: [],
      todo: [],
    };
    this.$scope.archivedTasksCount = 0;
    this.$scope.isDemoAccount = this.accountResolverService.getAccountData().type === AccountType.DemoAccount;

    const unsubscribe = this.$ngRedux.connect(this.bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.taskEvents.onTaskCreated((args) => this.onTaskCreated(args));
    this.taskEvents.onTaskDeleted((args) => this.onTaskDeleted(args));
    this.taskEvents.onTaskUpdated((args) => this.onTaskUpdated(args));
    this.$scope.$on("filterChanged", this.init);
    this.$scope.$on("closeFormTaskInKanban", this.closeFormTaskInKanban);

    this.$scope.createNewTaskInList = this.createNewTaskInList;
    this.$scope.openTaskFormInKanban = this.openTaskFormInKanban;
    this.$scope.dropped = this.dropped;
    this.$scope.deleteTask = this.deleteTask;
    this.$scope.toggleMode = this.toggleMode;
    this.$scope.archive = this.archive;
    this.$scope.clone = this.clone;
    this.$scope.setDueDate = this.setDueDate;
    this.$scope.sortBy = this.sortBy;
    this.$scope.increaseListLimit = this.increaseListLimit;
    this.$scope.increaseKanbanLimit = this.increaseKanbanLimit;
    this.$scope.toggleTaskCellHover = this.toggleTaskCellHover;
    this.$scope.favoritesManager = this.favoritesManager;
    this.$scope.getNavItem = this.getNavItem;
    this.$scope.taskIsFavorite = this.taskIsFavorite;
    this.$scope.isNewExperienceEnabled = this.isNewExperienceEnabled;
  }

  private checkingEnabledFeatures: boolean;
  private showNewExperience: boolean;

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState): Record<string, unknown> => {
    return {
      assigneesIdMap: state.assignees.map,
      currentSessionsIds: filterActiveSessionsAllStatuses(state.sessions.items).map((s) => s.id),
    };
  };

  private toggleMode = (mode: TasksViewMode): void => {
    if (mode === this.$scope.mode) {
      return;
    }

    this.$scope.mode = mode;
    this.setInitialRenderingLimits();
    this.currentUserService.setUserSetting({ taskView: this.$scope.mode });
  };

  private init = (): IPromise<unknown> => {
    this.$scope.indicators.loadingTasks = true;

    this.setInitialView();
    this.setInitialSort();

    let loadFilterPromise: IPromise<IHubFilter>;

    if (this.$stateParams.filterId) {
      loadFilterPromise = this.hubService.getFilter(this.$stateParams.filterId);
      this.$rootScope.$broadcast("hasToLoadFilter", this.$stateParams.filterId);
    } else {
      this.$rootScope.$broadcast("setFakeFilter");
    }

    this.$scope.isBannerEnabled = this.accountResolverService.getAccountData().type === AccountType.TrialAccount;

    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;
    const promises = {
      newExperienceEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperience),
      newExperienceToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
    };

    this.$q
      .all(promises)
      .then(({ newExperienceEnabled, newExperienceToggleEnabled }) => {
        const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
        this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
        this.checkingEnabledFeatures = false;
        if (this.showNewExperience) {
          this.favoritesManager.initFavorites();
        }
      })
      .catch(() => (this.checkingEnabledFeatures = false));

    return this.$q
      .all({
        reduxAssignees: this.$ngRedux.dispatch(this.assigneeActions.getAssignees()),
        reduxSessions: this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing()),
        loadFilter: loadFilterPromise,
      })
      .then(({ loadFilter }) => {
        let tasksOwner: string | string[];

        if (loadFilter) {
          tasksOwner = this.processFilterIds(loadFilter);
        } else {
          tasksOwner = getCurrentUserId();
        }

        return this.loadTasks(tasksOwner);
      });
  };

  private sortBy = (sort: TaskSort, changeAscDesSort = true): void => {
    if (this.sortTask?.includes(sort) && changeAscDesSort) {
      this.setAscDesSort(sort);
    } else {
      this.sortTask = sort;
    }

    Object.keys(this.$scope.isSortedBy).forEach((key) => {
      if (key === this.sortTask.replace("-", "")) {
        this.$scope.isSortedBy[key] = true;
      } else {
        this.$scope.isSortedBy[key] = false;
      }
    });

    this.$state.go(".", { tasksSortBy: this.sortTask }, { notify: false });
    this.currentUserService.setUserSetting({ taskOrder: this.sortTask });
    this.init();
  };

  private setAscDesSort = (sort: TaskSort): void => {
    if (this.sortTask.includes("-")) {
      this.sortTask = this.sortTask.replace("-", "") as TaskSort;
    } else {
      this.sortTask = `-${sort}` as TaskSort;
    }

    this.$scope.isAscSort = this.sortTask.includes("-");
  };

  private setInitialView = () => {
    if (this.currentUserService.getUserSetting("taskView")) {
      this.$scope.mode = this.currentUserService.getUserSetting("taskView");
    } else {
      this.$scope.mode = "list";
    }
  };

  private setInitialSort = () => {
    if (this.currentUserService.getUserSetting("taskOrder")) {
      this.sortTask = this.currentUserService.getUserSetting("taskOrder");

      const sortedByKey = this.sortTask.replace("-", "");
      this.$scope.isSortedBy[sortedByKey] = true;
      this.$scope.isAscSort = this.sortTask.includes("-");
    }
  };

  private processFilterIds = (filter: IHubFilter): string[] => {
    if (filter.employeeIds && filter.teamIds) {
      return filter.employeeIds.concat(filter.teamIds);
    }
    if (!filter.teamIds) {
      return filter.employeeIds;
    }
    if (!filter.employeeIds) {
      return filter.teamIds;
    }
  };

  private loadTasks(ownerId: string | string[]): IPromise<unknown> {
    const getTasksParams: IRestLayerRequest = {
      filter: {
        $and: [
          {
            ...(Array.isArray(ownerId) ? { ownerId: { $in: ownerId } } : { ownerId }),
          },
          {
            $or: [{ sessionId: { $exists: false } }, { sessionId: { $in: this.$scope.currentSessionsIds } }],
          },
        ],
      },
      sort: this.sortTask,
      // we want to get all tasks
      limit: -1,
      fields: "id,name,ownerId,dueDate,description,status,sessionId,parentId,parentName,parentType,externalId,externalSystem,archived",
    };

    return this.tasksService.getTasksV2(getTasksParams).then(
      (tasks) => {
        this.setInitialRenderingLimits();

        window.addEventListener("resize", this.onWindowResized);
        this.$scope.$on("$destroy", () => window.removeEventListener("resize", this.onWindowResized));

        let archivedTasksCount = 0;
        const activeTasks = [];

        for (const task of tasks.items) {
          task.archived ? archivedTasksCount++ : activeTasks.push(task);
        }
        this.$scope.archivedTasksCount = archivedTasksCount;
        this.generateTasksForBothViews(activeTasks);
        this.$scope.indicators.loadingTasks = false;
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "loadingTasks", inline: true })
    );
  }
  private generateTasksForBothViews = (tasks: ITask[]): void => {
    this.$scope.taskList = tasks;
    this.$scope.tasksColumns = {
      done: [],
      inprogress: [],
      todo: [],
    };

    const taskParentsPromises = [];

    for (const task of tasks) {
      this.$scope.tasksColumns[task.status]?.push(task);

      if (task.parentId) {
        taskParentsPromises.push(this.loadTaskParent(task.parentType, task.parentId));
      }
    }

    this.$q
      .all(taskParentsPromises)
      .then((taskParents) => {
        this.$scope.taskList.forEach((taskItem) => {
          const parentFound = taskParents.find((taskParent) => taskParent.id === taskItem.parentId);
          if (parentFound) {
            taskItem.parentName = parentFound.name;
          }
        });
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  };

  private onTaskCreated = (args: ITaskCreatedArgs): void => {
    this.increaseAllRenderingLimitsBy(1);

    if (args.task.parentId) {
      this.loadTaskParent(args.task.parentType, args.task.parentId).then((parent) => {
        args.task.parentName = parent.name;
        this.addTaskIntoArrays(args.task);
      });
    } else {
      this.addTaskIntoArrays(args.task);
    }

    this.$scope.isCreatingNewKanbanTask = false;
  };

  private addTaskIntoArrays = (task: ITask): void => {
    const taskStatus = task.status ? task.status : "todo";

    this.$scope.tasksColumns[taskStatus]?.unshift(task);
    this.$scope.taskList.push(task);
  };

  private loadTaskParent = (parentType: TaskParentType, parentId: string): IPromise<IGoal | IMetric> => {
    switch (parentType) {
      case "goal":
        return this.goalService.getGoalWithFields(parentId, "name").then((goal) => goal);
      case "metric":
        return this.metricService.getMetric(parentId).then((metric) => metric);
    }
  };

  private onTaskDeleted = (args: ITaskDeletedArgs): void => {
    this.removeTaskFromViews(args.task);
  };

  private deleteTask = (task: ITask): void => {
    interface IDeleteTaskScope extends IModalScope {
      delete(): void;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-delete-task.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IDeleteTaskScope) {
          $scope.delete = (): void => {
            ctrl.removeTaskFromViews(task);
            ctrl.tasksService.delete(task.id).then(() => {
              $scope.$close();
            });
          };
        },
      ],
    });
  };

  private removeTaskFromViews = (task: ITask): void => {
    for (let i = 0; i < this.$scope.taskList.length; i++) {
      if (this.$scope.taskList[i].id === task.id) {
        this.$scope.taskList.splice(i, 1);
      }
    }

    for (let i = 0; i < this.$scope.tasksColumns[task.status].length; i++) {
      if (this.$scope.tasksColumns[task.status][i].id === task.id) {
        this.$scope.tasksColumns[task.status].splice(i, 1);
      }
    }
  };

  private toggleTaskCellHover = (isTaskCellHovered: boolean): void => {
    this.$scope.isTaskCellHovered = isTaskCellHovered;
  };

  private openTaskFormInKanban = (): void => {
    if (this.$scope.isCreatingNewKanbanTask) {
      this.$scope.$broadcast("focusTextInput");
    } else {
      this.$scope.isCreatingNewKanbanTask = true;
    }
  };

  private closeFormTaskInKanban = (): void => {
    this.$scope.$apply(() => {
      this.$scope.isCreatingNewKanbanTask = false;
    });
  };

  private createNewTaskInList = (): void => {
    const newTask = {
      ownerId: getCurrentUserId(),
      name: "",
      description: "",
      status: "todo",
    };

    this.tasksService.create(newTask).then(
      (data) => {
        this.increaseAllRenderingLimitsBy(1);
        data.isNew = true;
        this.$scope.taskList.unshift(data);
        this.$scope.tasksColumns.todo.unshift(data);

        this.$timeout(() => document.querySelector<HTMLElement>(`#task${data.id} [ng-model='task.name']`).focus(), 200);
      },
      () => {
        // do something
      }
    );
  };

  private onTaskUpdated = (args: ITaskUpdatedArgs) => {
    // if broadcasted task i archived -> remove it from the view if present
    if (args.task.archived) {
      this.removeTaskFromViews(args.task);
      this.$scope.archivedTasksCount++;
      return;
    } else if (Object.keys(args.update).includes("archived")) {
      this.$scope.archivedTasksCount--;
    }

    if (args.task.parentId) {
      this.loadTaskParent(args.task.parentType, args.task.parentId).then((parent) => {
        args.task.parentName = parent.name;
        this.processTaskAfterUpdate(args);
      });
    } else {
      this.processTaskAfterUpdate(args);
    }
  };

  private processTaskAfterUpdate = (args: ITaskUpdatedArgs): void => {
    let taskFound = true;
    if (Object.keys(args.update).includes("archived")) {
      taskFound = false;
    }

    const indexTaskColumns = this.$scope.tasksColumns[args.task.status]?.findIndex((task: ITask) => task.id === args.task.id);
    const indexTaskList = this.$scope.taskList.findIndex((task: ITask) => task.id === args.task.id);

    if (indexTaskColumns !== undefined && indexTaskColumns !== -1) {
      taskFound = true;
      this.$scope.tasksColumns[args.task.status][indexTaskColumns] = args.task;
    }

    if (indexTaskList !== -1) {
      taskFound = true;
      this.$scope.taskList[indexTaskList] = args.task;
    }

    // if task is not present -> add it to collections
    if (!taskFound) {
      this.onTaskCreated({ task: args.task, sessionId: args.sessionId });
    }

    // if status is changed -> move task to the column it belongs and sort the list
    if (Object.keys(args.update).includes("status")) {
      this.onTaskStatusChanged(args.task);
    }
  };

  private dropped = (event, index, type, external, column: string): void => {
    for (let i = 0; i < this.$scope.tasksColumns[column].length; i++) {
      if (this.$scope.tasksColumns[column][i].status !== column) {
        this.$scope.tasksColumns[column][i].status = column;

        this.updateTask(this.$scope.tasksColumns[column][i], "status");
        this.updateListTask(this.$scope.tasksColumns[column][i]);

        // animation for done
        if (column === "done") {
          this.$timeout(() => {
            document.getElementById(this.$scope.tasksColumns[column][i].id).classList.add("celebration");
            this.$timeout(() => {
              document.getElementById(this.$scope.tasksColumns[column][i].id).classList.remove("celebration");
            }, 900);
          }, 120);
        }
      }
    }
  };

  private setDueDate = (task: ITask, date: string): void => {
    this.$scope.dueDate = date;
    this.$scope.taskList.forEach((t, i) => {
      if (t.id === task.id) {
        this.$scope.taskList[i].dueDate = date;
      }
    });

    this.updateTask(task, "dueDate");
  };

  private updateListTask = (task) => {
    for (let i = 0; i < this.$scope.taskList.length; i++) {
      if (this.$scope.taskList[i].id === task.id) {
        this.$scope.taskList[i] = task;
      }
    }
  };

  private updateTask = (task, field) => {
    const update = { [field]: task[field] };
    this.tasksService.update(task.id, update);
  };

  private onTaskStatusChanged = (changedTask: ITask) => {
    if (this.sortTask && this.sortTask.includes("status")) {
      this.sortBy(this.sortTask, false);
      return;
    }

    for (const task of this.$scope.taskList) {
      if (task.id === changedTask.id) {
        task.status = changedTask.status;
      }
    }

    this.$scope.tasksColumns = {
      done: [],
      inprogress: [],
      todo: [],
    };

    for (const task of this.$scope.taskList) {
      this.$scope.tasksColumns[task.status]?.push(task);
    }
  };

  private archive = (task: ITask) => {
    this.removeTaskFromViews(task);
    task.archived = true;
    this.updateTask(task, "archived");
    this.$scope.archivedTasksCount++;
  };

  private clone = (task: ITask): void => {
    const clonedTask = angular.copy(task);

    this.tasksService.create(clonedTask, { gtmhubAdditionalParams: { cloned: true } }).then(
      (newTask) => {
        this.increaseAllRenderingLimitsBy(1);
        newTask.isNew = true;
        this.$scope.taskList.push(newTask);
        this.$scope.tasksColumns[newTask.status]?.unshift(newTask);

        this.$timeout(() => document.querySelector<HTMLElement>(`#task${newTask.id} [ng-model='task.name']`).focus(), 200);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private setInitialRenderingLimits = (): void => {
    const { listLimitStep, kanbanLimitStep } = this.calculateRenderingSteps();
    this.$scope.renderingOptions = {
      list: { limitTo: listLimitStep, step: listLimitStep },
      kanban: {
        todo: { limitTo: kanbanLimitStep },
        inprogress: { limitTo: kanbanLimitStep },
        done: { limitTo: kanbanLimitStep },
        step: kanbanLimitStep,
      },
    };
  };

  private calculateRenderingSteps = (): { listLimitStep: number; kanbanLimitStep: number } => {
    const windowHeight = document.documentElement.clientHeight;
    const enlargementCoefficient = 1.1;

    return {
      listLimitStep: Math.ceil((windowHeight * enlargementCoefficient) / sizes.taskRowHeight),
      kanbanLimitStep: Math.ceil((windowHeight * enlargementCoefficient) / sizes.taskKanbanHeight),
    };
  };

  private increaseListLimit = (): void => {
    if (this.$scope.renderingOptions.list.limitTo < this.$scope.taskList.length) {
      this.$scope.renderingOptions.list.limitTo += this.$scope.renderingOptions.list.step;
    }
  };

  private increaseKanbanLimit = (columnName: GtmhubTaskStatus): void => {
    if (this.$scope.renderingOptions.kanban[columnName].limitTo < this.$scope.tasksColumns[columnName].length) {
      this.$scope.renderingOptions.kanban[columnName].limitTo += this.$scope.renderingOptions.kanban.step;
    }
  };

  private increaseAllRenderingLimitsBy = (by: number): void => {
    this.$scope.renderingOptions.list.limitTo += by;
    this.$scope.renderingOptions.kanban.todo.limitTo += by;
    this.$scope.renderingOptions.kanban.inprogress.limitTo += by;
    this.$scope.renderingOptions.kanban.done.limitTo += by;
  };

  private onWindowResized = (): void => {
    const { listLimitStep, kanbanLimitStep } = this.calculateRenderingSteps();
    if (this.$scope.renderingOptions.list.step < listLimitStep) {
      this.$scope.renderingOptions.list.step = listLimitStep;
    }
    if (this.$scope.renderingOptions.kanban.step < kanbanLimitStep) {
      this.$scope.renderingOptions.kanban.step = kanbanLimitStep;
    }
  };

  private getNavItem = (task: ITask): INavItem => {
    return this.favoritesManager.getFavoriteItem({ id: task.id }) || navItemsFactory.withRandomColor(navItemsFactory.buildTaskNavItem(task));
  };

  private taskIsFavorite = (task: ITask): boolean => {
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite({ id: task.id });
  };

  private isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };
}
